import { useEffect, useMemo, useRef } from 'react';

type DebouncedFn<Args extends unknown[]> = ((...args: Args) => void) & {
  cancel: () => void;
  flush: () => void;
};

type Options = {
  /**
   * If true, any pending invocation is fired synchronously when the component
   * unmounts. Default false (the timer is cancelled and the call is dropped).
   * Use this for autosave-on-blur style hooks where dropping the last edit
   * would be data loss.
   */
  flushOnUnmount?: boolean;
};

/**
 * Returns a stable debounced wrapper around `fn`. Each call resets the timer;
 * the latest `fn` is always invoked when the timer fires. `ms` may be a
 * function of the call's arguments, so each call can set its own delay.
 *
 * Sibling to react-use's value-based `useDebounce` — use this when you need a
 * callable debounced function (e.g. inside a non-render subscription callback).
 */
export const useDebounceCallback = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  ms: number | ((...args: Args) => number),
  options: Options = {}
): DebouncedFn<Args> => {
  const { flushOnUnmount = false } = options;

  const fnRef = useRef(fn);
  fnRef.current = fn;
  const msRef = useRef(ms);
  msRef.current = ms;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<Args | null>(null);

  const debounced = useMemo(() => {
    const cancel = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      lastArgsRef.current = null;
    };

    const flush = () => {
      if (timerRef.current && lastArgsRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        const args = lastArgsRef.current;
        lastArgsRef.current = null;
        fnRef.current(...args);
      }
    };

    const invoke = (...args: Args) => {
      lastArgsRef.current = args;
      if (timerRef.current) clearTimeout(timerRef.current);
      const delay =
        typeof msRef.current === 'function'
          ? msRef.current(...args)
          : msRef.current;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        lastArgsRef.current = null;
        fnRef.current(...args);
      }, delay);
    };

    return Object.assign(invoke, { cancel, flush });
  }, []);

  // Refs over the stable `debounced` reference so the unmount effect picks up
  // the current timer/args without re-running on every render.
  useEffect(() => {
    return () => {
      if (flushOnUnmount) debounced.flush();
      else debounced.cancel();
    };
  }, [debounced, flushOnUnmount]);

  return debounced;
};
